// ============================================================================
// Board vocabulary — the labels, ordering, and worded tags for the three board
// overlays: attention (what a booking owes a human), SLA standing, and the
// native board sections. Worded, never colour-only — the board is scanned by
// non-technical ops across many rows (same principle as logistics_status_tag).
// ============================================================================
import { Tag, Tooltip } from "antd";
import React from "react";
import { AttentionBucket, BookingEscalation, BoardSection, SlaState } from "../../types";

// --- Attention (the ONE action a booking owes) ------------------------------
export const ATTENTION_META: Record<
  AttentionBucket,
  { color?: string; label: string; help: string }
> = {
  failed: {
    color: "red",
    label: "Failed",
    help: "The carrier call failed — requeue it, or move it to another carrier.",
  },
  needs_booking: {
    color: "volcano",
    label: "Needs booking",
    help: "Book this on the partner's app, then confirm it (with the vehicle) in Pending Tasks.",
  },
  needs_confirm: {
    color: "orange",
    label: "Needs confirm",
    help: "The partner has to action a cancel / change — confirm it once done.",
  },
  needs_vehicle: {
    color: "gold",
    label: "Needs vehicle",
    help: "A booked own-fleet truck with no vehicle yet, and dispatch is close. Assign one.",
  },
  awaiting_proof: {
    color: "blue",
    label: "Awaiting proof",
    help: "Delivered, but no proof-of-delivery on file yet. Upload the POD.",
  },
  advancing: {
    color: "cyan",
    label: "In flight",
    help: "A manual booking in progress — move its status by hand as it picks up / delivers.",
  },
  none: { label: "", help: "" },
};

// The order the attention chips appear in the strip (most urgent first).
export const ATTENTION_ORDER: AttentionBucket[] = [
  "failed",
  "needs_booking",
  "needs_confirm",
  "needs_vehicle",
  "awaiting_proof",
  "advancing",
];

export const AttentionTag: React.FC<{ attention: AttentionBucket }> = ({ attention }) => {
  const meta = ATTENTION_META[attention];
  if (!meta || attention === "none") return null;
  return (
    <Tooltip title={meta.help}>
      <Tag color={meta.color} style={{ marginInlineEnd: 0, cursor: "help" }}>
        {meta.label}
      </Tag>
    </Tooltip>
  );
};

// --- SLA standing (against the leg's due date) ------------------------------
export const SLA_META: Record<SlaState, { color?: string; label: string }> = {
  breached: { color: "red", label: "SLA breached" },
  due_soon: { color: "gold", label: "Due soon" },
  on_track: { color: "green", label: "On track" },
  none: { label: "" },
};

export const SlaTag: React.FC<{ sla: SlaState }> = ({ sla }) => {
  const meta = SLA_META[sla];
  if (!meta || sla === "none" || sla === "on_track") return null; // only surface what needs eyes
  return (
    <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
      {meta.label}
    </Tag>
  );
};

// --- Escalations (an operator's "somebody has to look at this truck") -------
// The row-level echo of the escalation section: a truck can also be CLOSED (cancelled) while an
// escalation is still open on it, and the tag is what explains that row where the section can't.
// Worded and tooltipped, like every other overlay here — never colour alone.
export const EscalationTag: React.FC<{ escalation: BookingEscalation | null }> = ({
  escalation,
}) => {
  if (!escalation) return null;
  const raiser = escalation.raised_by || "the system";
  return (
    <Tooltip
      title={
        <>
          {escalation.issue_type || "Escalation"} — raised by {raiser}
          {escalation.remarks ? <div style={{ marginTop: 4 }}>“{escalation.remarks}”</div> : null}
        </>
      }
    >
      <Tag color="red" style={{ marginInlineEnd: 0, cursor: "help" }}>
        Escalated{escalation.count > 1 ? ` ×${escalation.count}` : ""}
      </Tag>
    </Tooltip>
  );
};

// --- Board sections (native trip-planning groupings) ------------------------
export const SECTION_META: Record<BoardSection, { label: string; help?: string }> = {
  // FIRST on both boards, and it outranks every working section: a truck somebody raised an
  // escalation on has left ordinary trip-planning, and leaving it in to-assign / on-a-truck is how a
  // known-broken delivery gets planned as if nothing were wrong. Each row carries WHAT the problem
  // is; "Resolve escalation" (row menu) is the way back to its working section.
  escalation: {
    label: "Escalations — needs a decision",
    help:
      "Somebody raised an escalation on these trucks, so they are out of the normal queue " +
      "until it's resolved. The row shows the issue, the note, and who flagged it.",
  },
  last_mile_to_assign: {
    label: "Last mile — to assign",
    help: "Ready to go to the customer, but no vehicle / trip yet.",
  },
  last_mile_on_truck: {
    label: "Last mile — on a truck",
    help: "On its way to the customer (vehicle assigned / dispatched).",
  },
  first_mile_to_assign: {
    label: "First mile — to assign",
    help: "Supplier pickup into an MD warehouse, but no vehicle / trip yet.",
  },
  first_mile_on_truck: {
    label: "First mile — on a truck",
    help: "Inbound pickup on the move (vehicle assigned / dispatched).",
  },
  first_mile_vendor_delivers: {
    label: "First mile — vendor delivers",
    help:
      "The supplier brings the goods into our warehouse themselves — no pickup truck of ours " +
      "to plan; chase the vendor against their promised date.",
  },
  transfer: { label: "Transfers", help: "Warehouse to warehouse." },
  // Appears on BOTH boards, each holding its own half: an outstation order runs a hop that feeds a
  // warehouse (First Mile) AND a leg that carries the goods on to the customer (Last Mile), so the
  // lane follows the leg and the section follows the partner. Do not read this as one lane's set.
  outstation: {
    label: "Outstation",
    help:
      "Intercity / part-load booked with a partner — the inbound hop on First Mile, " +
      "the customer-bound leg on Last Mile.",
  },
  returns: { label: "Returns", help: "Coming back from the customer." },
  held: {
    label: "Technical Error - Needs attention",
    help: "A carrier call failed — needs a human.",
  },
  closed: { label: "Cancelled", help: "Torn-down bookings." },
  other: { label: "Other" },
};

// Section display order on the board (actionable / in-progress first; cancelled last).
// A RECORD, not a hand-written array: every BoardSection must carry a rank, so adding a section
// without placing it is a compile error rather than rows that quietly render nowhere.
const SECTION_RANK: Record<BoardSection, number> = {
  // Escalations first — they are the thing ops has to decide on before any of the planning below
  // means anything, and they are the smallest section, so they never bury the rest of the board.
  escalation: 0,
  held: 1,
  last_mile_to_assign: 2,
  last_mile_on_truck: 3,
  first_mile_to_assign: 4,
  first_mile_on_truck: 5,
  first_mile_vendor_delivers: 6,
  transfer: 7,
  outstation: 8,
  returns: 9,
  other: 10,
  closed: 11,
};

export const SECTION_ORDER: BoardSection[] = (Object.keys(SECTION_RANK) as BoardSection[]).sort(
  (a, b) => SECTION_RANK[a] - SECTION_RANK[b]
);
